import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import type { Brand } from '../../config/supabase';

// Query Keys
export const brandsKeys = {
  all: ['brands'] as const,
  lists: () => [...brandsKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number, search?: string) => 
    [...brandsKeys.lists(), { page, pageSize, search }] as const,
};

// Fetch all brands (for dropdowns)
export const useBrandsAll = () => {
  return useQuery({
    queryKey: brandsKeys.list(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brands')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    // Cache for 5 minutes since brands don't change often
    staleTime: 5 * 60 * 1000,
    // Prevent background refetches during development
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Fetch brands with pagination
export const useBrands = (page: number = 1, pageSize: number = 10, search: string = '') => {
  return useQuery({
    queryKey: brandsKeys.list(page, pageSize, search),
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('brands')
        .select('*', { count: 'exact' });

      // Apply search filter if search term exists
      if (search.trim()) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      query = query
        .order('name')
        .range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      
      return {
        brands: data || [],
        totalCount: count || 0,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
};

// Create brand mutation
export const useCreateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brandData: Omit<Brand, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('brands')
        .insert(brandData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandsKeys.all });
    },
  });
};

// Update brand mutation
export const useUpdateBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Brand> & { id: string }) => {
      const { data, error } = await supabase
        .from('brands')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandsKeys.all });
    },
  });
};

// Delete brand mutation
export const useDeleteBrand = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('brands')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandsKeys.all });
    },
  });
}; 